import _ from "lodash";
import {AllInterferometricStacksType, InterferometricStackType, ProductImageStacType} from "../types/product";
import {
  getProductIdentifierFrom, intersectAllProductImages,
  isSameProductImageOf,
  sortProductImagesByAcquisitionDateStartDESC
} from "./productHelper";
import {AdvancedSearchConfigurationsType, ConfigurationsType, MenuSearchFieldType} from "../types/configurations";
import {
  getMissionCodesByDescriptions,
  getScopeConfigurationFieldsBy,
  getSearchFieldConfigurationsBy,
  hasAtLeastOneMissionSelected,
  isHandleExternalSearchField,
} from "./configurationHelper";
import {stackUpAllGeoJSONItems} from "./geoJSONHelper";
import {GeoJSONType} from "../types/geoJSON";
import {ShapeType} from "../types/shape";
import {objectValues} from "./utilsHelper";
import {AppParametersType} from "../types/app";

// return true if stack view and image view are available for all missions selected
export const areBothImageAndStackSearchAvailable = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeConfigurationFields: string,
  formValues: any,
): boolean => {
  return formValues.routine && hasAtLeastOneMissionSelected(formValues) && formValues.missions.every((mission) => {
    const formValuesWithOneMission = {...formValues, missions: [mission]};
    const searchFieldConfig = getSearchFieldConfigurationsBy(configurations, appParameters, scopeConfigurationFields, "ENABLE_STACK_VIEW", formValuesWithOneMission);
    return !!searchFieldConfig && (searchFieldConfig as MenuSearchFieldType).possibleValues.length === 2;
  });
};

export const isOnlyStackSearchAvailable = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeConfigurationFields: string,
  formValues: any,
): boolean => {
  return formValues.routine && hasAtLeastOneMissionSelected(formValues) && formValues.missions.every((mission) => {
    const formValuesWithOneMission = {...formValues, missions: [mission]};
    const searchFieldConfig = getSearchFieldConfigurationsBy(configurations, appParameters, scopeConfigurationFields, "ENABLE_STACK_VIEW", formValuesWithOneMission);
    return !!searchFieldConfig &&
      (searchFieldConfig as MenuSearchFieldType).possibleValues.length === 1 &&
      (searchFieldConfig as MenuSearchFieldType).possibleValues[0] === "TRUE"
  });
};

export const isStackEnabledOnMission = (
    configurations: ConfigurationsType,
    appParameters: AppParametersType,
    scopeConfigurationFields: string,
    formValues: any,
): boolean => {
  return hasAtLeastOneMissionSelected(formValues) && formValues.missions.every((mission) => {
    const formValuesWithOneMission = {...formValues, missions: [mission]};
    const searchFieldConfig = getSearchFieldConfigurationsBy(configurations, appParameters, scopeConfigurationFields, "ENABLE_STACK_VIEW", formValuesWithOneMission);
    return !!searchFieldConfig &&
        (searchFieldConfig as MenuSearchFieldType).possibleValues.length > 1 && (searchFieldConfig as MenuSearchFieldType).isApplicable
  });
};

export const isOnlyImageSearchAvailable = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeConfigurationFields: string,
  formValues: any,
): boolean => {
  return !isOnlyStackSearchAvailable(configurations, appParameters, scopeConfigurationFields, formValues) &&
    !areBothImageAndStackSearchAvailable(configurations, appParameters, scopeConfigurationFields, formValues);
};

export const getInterferometricStackIdentifierFrom = (
  stack: InterferometricStackType
): string => {
  return stack.items.reduce((acc: string, item) => {
    const itemID = getProductIdentifierFrom(item);
    return !!itemID
      ? `${acc}${itemID}`
      : acc
  }, "")
};

export const getInterferometricStackItemsSelected = (
  stack: InterferometricStackType,
  selectedProducts: ProductImageStacType[]
): ProductImageStacType[] => {
  return stack.items.filter((item) => {
    return selectedProducts.some(isSameProductImageOf(item))
  });
};

export const getInterferometricStackItemsSelectedCount = (
  stack: InterferometricStackType,
  selectedProducts: ProductImageStacType[]
): number => {
  return getInterferometricStackItemsSelected(stack, selectedProducts).length;
};

export const isInterferometricStackSelected = (
  stack: InterferometricStackType,
  selectedProducts: ProductImageStacType[]
): boolean => {
  return getInterferometricStackItemsSelectedCount(stack, selectedProducts) > 0;
};

export const getInterferometricStackItemsCount = (
  stack: InterferometricStackType
) => {
  return (stack && stack.items && stack.items.length) || 0;
};

export const findInterferometricStackFrom = (
  allInterferometricStacks: AllInterferometricStacksType,
  productImage: ProductImageStacType
) => {
  return objectValues(allInterferometricStacks).find((stack) => isInterferometricStackSelected(stack, [productImage]));
};

export const getAllInterferometricStacksSelected = (
  allInterferometricStacks: AllInterferometricStacksType,
  selectedProducts: ProductImageStacType[]
): InterferometricStackType[] => {
  return objectValues(allInterferometricStacks).filter((stack) => isInterferometricStackSelected(stack, selectedProducts));
};

export const findIndexOfInterferometricStackFrom = (
  interferometricStacks: InterferometricStackType[],
  interferometricStack: InterferometricStackType
): number => {
  return interferometricStacks.findIndex((stack) => (
    getInterferometricStackIdentifierFrom(stack) === getInterferometricStackIdentifierFrom(interferometricStack)
  ));
};

export const changeProductImageGeometryWith = (
  productImage: ProductImageStacType,
  geoJSON: GeoJSONType | null
): ProductImageStacType | null => {
  return geoJSON && geoJSON.geometry
    ? {
      ...productImage,
      geometry: geoJSON.geometry
    }
    : null
};

export const getProductImageFromInterferometricStack = (
  configurations: AdvancedSearchConfigurationsType,
  appParameters: AppParametersType,
  interferometricStack: InterferometricStackType,
  selectedProducts: ProductImageStacType[],
  selectedArea: ShapeType
): ProductImageStacType | null => {
  let interferometricStackItems: ProductImageStacType[] = [];

  if (!_.isEmpty(selectedProducts)) {
    interferometricStackItems = getInterferometricStackItemsSelected(interferometricStack, selectedProducts);
  }

  // corner case:
  // the user center the stack but doesn't select any product images of that stack.
  // if the is no selected products for the interferometric stack then take all items of such interferometric stack
  if (_.isEmpty(interferometricStackItems)) {
    interferometricStackItems = interferometricStack.items;
  }

  const missionFormValues = {missions: getMissionCodesByDescriptions(configurations, [interferometricStack.mission])};
  const stripProductImages = getStripProductImages(
    configurations,
    appParameters,
    missionFormValues,
    selectedArea,
    true,
    interferometricStackItems
  );

  return intersectAllProductImages(stripProductImages);
};

export const getStripProductImages = (
  configurations,
  appParameters: AppParametersType,
  missionFormValues,
  selectedArea,
  isInterferometricStackSearch,
  interferometricStackItems
): (ProductImageStacType | null)[] => {
  return doStackUpAllProductImagesBySameStrip(configurations, appParameters, missionFormValues, selectedArea, isInterferometricStackSearch)
    ? stackUpAllProductImagesGroupedBySameStrip(interferometricStackItems) // for Sentinel-1
    : interferometricStackItems; // for CSK
}

export const stackUpAllProductImagesGroupedBySameStrip = (
  interferometricStackProductsSelected: ProductImageStacType[]
): (ProductImageStacType | null)[] => {
  return groupProductImagesBySameStrip(interferometricStackProductsSelected)
    .map((products) => {
      const sortedProducts = sortProductImagesByAcquisitionDateStartDESC(products);
      const stakedUpShape = stackUpAllGeoJSONItems(sortedProducts);
      return changeProductImageGeometryWith(products[0], stakedUpShape);
    }) || [];
};

const groupProductImagesBySameStrip = (
  interferometricStackProducts: ProductImageStacType[],
): ProductImageStacType[][] => {
  const productSelectedGroupBySameStripMap: {[key:string]: ProductImageStacType[]} = interferometricStackProducts
    .reduce((acc, product: ProductImageStacType) => {
      const relative_orbit_number = product && product.properties && product.properties.relative_orbit_number;
      const acqDate = product && product.properties && product.properties.start_datetime && product.properties.start_datetime.split("T")[0];
      const groupID = `${acqDate}-${relative_orbit_number}`;
      acc[groupID] = (acc[groupID] || []).concat([product])
      return acc;
    }, {});

  return objectValues(productSelectedGroupBySameStripMap);
}

export const doStackUpAllProductImagesBySameStrip = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  formValues: any,
  selectedArea: ShapeType,
  isInterferometricStackSearch: boolean
) => {
  // @mapitaly: sentinel-1 is not a choice
  return (isInterferometricStackSearch && false // isOnlySentinelMissionSelected(formValues)
      ) ||
    (!isInterferometricStackSearch && canStackUpProductImages(configurations, appParameters, formValues, selectedArea));
}

const canStackUpProductImages = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  formValues: any,
  selectedArea
): boolean => {
  const scopeConfigurationFields = getScopeConfigurationFieldsBy(configurations);
  const searchField = getSearchFieldConfigurationsBy(configurations,  appParameters, scopeConfigurationFields, "MERGE_IMAGES", formValues) as MenuSearchFieldType;
  return isHandleExternalSearchField(configurations,  appParameters, scopeConfigurationFields, "MERGE_IMAGES", formValues, selectedArea) &&
    searchField && searchField.possibleValues.length === 1 && searchField.possibleValues[0] === "TRUE"
}
